import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function NewDemoAccount() {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState(null);

  const [demoName, setDemoName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");

  const suggestedSubdomain = useMemo(() => {
    return String(demoName || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  }, [demoName]);

  const fullDomainPreview = subdomain
    ? `${String(subdomain).trim().toLowerCase()}.agentbookcrm.com`
    : "";

  const checkSubdomain = async (value = subdomain) => {
    try {
      const trimmed = String(value || "").trim();

      if (!trimmed) {
        setSubdomainStatus(null);
        return null;
      }

      setCheckingSubdomain(true);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/api/demo-accounts/check-subdomain?value=${encodeURIComponent(trimmed)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to check subdomain");
      }

      setSubdomainStatus(data);
      return data;
    } catch (err) {
      console.error("Check demo subdomain error:", err.message);
      const failed = {
        available: false,
        reason: err.message || "Failed to check subdomain"
      };
      setSubdomainStatus(failed);
      return failed;
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const useSuggestedSubdomain = () => {
    if (!suggestedSubdomain) return;
    setSubdomain(suggestedSubdomain);
    setSubdomainStatus(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!demoName.trim()) {
        throw new Error("Demo name is required");
      }

      if (!subdomain.trim()) {
        throw new Error("Subdomain is required");
      }

      const subdomainResult = await checkSubdomain(subdomain);

      if (!subdomainResult?.available) {
        throw new Error(subdomainResult?.reason || "That subdomain is not available");
      }

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/demo-accounts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          demo_name: demoName,
          subdomain,
          expires_at: expiresAt || null,
          notes: notes || null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to create demo account");
      }

      navigate(`/demo-accounts/${data.id || data.demo_account?.id}`);
    } catch (err) {
      console.error("Create demo account error:", err.message);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">New Demo Account</h1>
            <p className="sales-subtitle">
              Create a separate internal demo environment that stays out of subscriber reporting.
            </p>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/demo-accounts")}>
              Cancel
            </button>
            <button className="sales-primary-btn" onClick={handleSave} disabled={saving || checkingSubdomain}>
              {saving ? "Creating..." : "Create Demo Account"}
            </button>
          </div>
        </div>

        <div className="sales-form-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Demo Information</h2>
              <p>Name the demo environment and reserve its subdomain.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Demo Name *</label>
                <input value={demoName} onChange={(e) => setDemoName(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Subdomain *</label>
                <input
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomain(e.target.value);
                    setSubdomainStatus(null);
                  }}
                  onBlur={() => checkSubdomain()}
                  placeholder="florida-aca-demo"
                />
                {checkingSubdomain && <small>Checking subdomain...</small>}
                {!checkingSubdomain && subdomainStatus?.available && (
                  <small style={{ color: "#15803d" }}>Subdomain is available.</small>
                )}
                {!checkingSubdomain && subdomainStatus && !subdomainStatus.available && (
                  <small style={{ color: "#b91c1c" }}>{subdomainStatus.reason || "Subdomain is unavailable."}</small>
                )}
              </div>

              <div className="sales-field">
                <label>Suggested Subdomain</label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input value={suggestedSubdomain} readOnly />
                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={useSuggestedSubdomain}
                    disabled={!suggestedSubdomain}
                  >
                    Use
                  </button>
                </div>
              </div>

              <div className="sales-field sales-field-full">
                <label>Full Domain</label>
                <input value={fullDomainPreview} readOnly />
              </div>

              <div className="sales-field">
                <label>Expires At</label>
                <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </div>

              <div className="sales-field sales-field-full">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="4"
                  style={{ width: "100%", resize: "vertical" }}
                />
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Launch Behavior</h2>
              <p>Demo accounts are opened from the platform in a new tab without a second login.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Access Model</label>
                <input value="Platform-controlled demo launch in a new tab" readOnly />
              </div>

              <div className="sales-field sales-field-full">
                <label>Login Behavior</label>
                <input value="No separate demo login required" readOnly />
              </div>

              <div className="sales-field sales-field-full">
                <label>Seeded Data</label>
                <input value="Blank demo environment for MVP" readOnly />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewDemoAccount;